/**
 * ZPL Builder for Label Printing
 * Creates ZPL (Zebra Programming Language) commands for QZ Tray printing
 * ZPL uses dots as unit (203 DPI = 8 dots/mm, 300 DPI = 12 dots/mm)
 */

import dayjs from 'dayjs'
import { isOptionEnabled } from '../helper'
import { getTableReceiptLabel } from './receiptTableHelper'

// DPI conversion: 203 DPI = 8 dots/mm, 300 DPI = 12 dots/mm
const DOTS_PER_MM_203 = 8 // Standard for most ZPL printers
const DOTS_PER_MM_300 = 12 // High resolution printers

/**
 * Preset keys untuk ukuran kertas label (digunakan untuk UI/options)
 */
const LABEL_PRESET_KEYS = [
  '33x15mm',
  '33x19mm',
  '38x60mm',
  '40x30mm',
  '40x40mm',
  '50x20mm',
  '50x30mm',
  '55x20mm',
  '55x30mm',
  '55x40mm',
  '60x40mm',
  '70x30mm',
  '78x60mm',
]

/**
 * Ambil widthMm dan heightMm dari string paper size (preset atau custom).
 * Format: "WxHmm" atau "WxH" (contoh: "40x30mm", "80x100mm", "40x30")
 * @param {string} paperSize - String ukuran kertas
 * @returns {{ widthMm: number, heightMm: number } | null}
 */
export const getLabelDimensions = (paperSize) => {
  if (!paperSize) {
    return null
  }

  const sizeStr = String(paperSize).toLowerCase().replace(/mm/g, '').trim()
  const match = sizeStr.match(/(\d+)x(\d+)/)

  if (match) {
    const widthMm = parseInt(match[1], 10)
    const heightMm = parseInt(match[2], 10)
    if (widthMm > 0 && heightMm > 0) {
      return { widthMm, heightMm }
    }
  }

  return null
}

/**
 * LABEL_PRESETS - object preset untuk backward compatibility.
 * Generated dari LABEL_PRESET_KEYS via getLabelDimensions.
 */
export const LABEL_PRESETS = Object.fromEntries(
  LABEL_PRESET_KEYS.map((key) => [key, getLabelDimensions(key)]).filter(([, v]) => v != null),
)

/**
 * Convert mm to dots (ZPL unit)
 * @param {number} mm - Millimeters
 * @param {number} dpi - DPI (default: 203)
 * @returns {number} Dots
 */
const mmToDots = (mm, dpi = 203) => {
  const dotsPerMm = dpi === 300 ? DOTS_PER_MM_300 : DOTS_PER_MM_203
  return Math.round(mm * dotsPerMm)
}

/**
 * Escape special characters for ZPL
 * ZPL uses ^ and ~ as control characters, so we need to escape them
 */
const escapeZPL = (text) => {
  if (!text) {
    return ''
  }
  return String(text)
    .replace(/\^/g, '^^') // Escape ^
    .replace(/~/g, '~~') // Escape ~
}

/**
 * Wrap text by words to fit within character width
 * @param {string} text - Text to wrap
 * @param {number} maxChars - Maximum characters per line
 * @returns {Array<string>} Array of wrapped lines
 */
const wrapWords = (text = '', maxChars = 20) => {
  const t = String(text ?? '').trim()
  if (!t) {
    return ['']
  }

  // Ensure maxChars is at least 1
  const safeMaxChars = Math.max(1, Math.floor(maxChars))

  // If text fits in one line, return as is
  if (t.length <= safeMaxChars) {
    return [t]
  }

  const words = t.split(/\s+/).filter((w) => w.length > 0)
  if (words.length === 0) {
    return ['']
  }

  const lines = []
  let currentLine = ''

  words.forEach((word) => {
    // If single word is longer than maxChars, split it
    if (word.length > safeMaxChars) {
      // Push current line if exists
      if (currentLine.trim()) {
        lines.push(currentLine.trim())
        currentLine = ''
      }
      // Split long word character by character
      let remainingWord = word
      while (remainingWord.length > safeMaxChars) {
        lines.push(remainingWord.substring(0, safeMaxChars))
        remainingWord = remainingWord.substring(safeMaxChars)
      }
      if (remainingWord) {
        currentLine = remainingWord
      }
      return
    }

    // Try to add word to current line
    const testLine = currentLine ? `${currentLine} ${word}` : word

    if (testLine.length <= safeMaxChars) {
      currentLine = testLine
      return
    }

    // Current line is full, push it and start new line with current word
    if (currentLine.trim()) {
      lines.push(currentLine.trim())
    }
    currentLine = word
  })

  // Push remaining line
  if (currentLine.trim()) {
    lines.push(currentLine.trim())
  }

  return lines.length > 0 ? lines : [t] // Fallback to original text if no lines
}

/**
 * Build ZPL command for a single label
 * Returns array of strings (like buildReceiptCommands in escposBuilder.js)
 * @param {Object} labelData - Label data { sequence, qty, productName, contactName, timestampText? }
 * @param {Object} options - Options { paperWidth, dpi, invert }
 * @returns {Array<string>} Array of ZPL command strings, each ending with \n
 */
export const buildZPLLabel = (labelData, options = {}) => {
  const { paperWidth, dpi = 203, invert = false } = options

  // Parse paper size
  const paperSize = getLabelDimensions(paperWidth)
  if (!paperSize) {
    // Default to 40x30mm
    return buildZPLLabel(labelData, { ...options, paperWidth: '40x30mm' })
  }

  const { widthMm } = paperSize

  // Convert to dots
  const widthDots = mmToDots(widthMm, dpi)
  // const heightDots = mmToDots(heightMm, dpi)

  // ZPL commands array - use \n for proper line endings
  const commands = []

  // ZPL commands
  // ^XA: Start Label Format
  // ^CI28: UTF-8 encoding
  // ^PW: Print Width (in dots)
  // ^POI: Print Orientation Invert (if invert is true)
  // ^FO: Field Origin (x, y)
  // ^A0N: Font A, Normal orientation
  // ^FD: Field Data
  // ^FS: Field Separator
  // ^XZ: End Label Format
  commands.push('^XA\n')
  commands.push('^CI28\n') // UTF-8 encoding
  commands.push(`^PW${widthDots}\n`) // Print width in dots

  // Invert orientation if needed (for fixing "terbalik" issue)
  if (invert) {
    commands.push('^POI\n') // Print Orientation Invert
  }

  // Calculate positions (in dots)
  const leftX = mmToDots(2, dpi) // Left align: 2mm from left edge
  const rightMargin = mmToDots(2, dpi) // 2mm right margin
  const availableWidthDots = widthDots - leftX - rightMargin

  // Font sizes (in dots, approximate)
  // ZPL font sizes: ^A0N,height,width
  // Use smaller font for better fit (similar to TSPL font "1")
  const fontHeight = widthMm <= 40 ? mmToDots(2, dpi) : mmToDots(2.5, dpi) // Smaller font
  const fontWidth = Math.round(fontHeight * 0.7) // Aspect ratio

  // Calculate max characters per line based on label width
  // Very conservative calculation to prevent text cutoff
  // Based on actual testing: 40mm label can fit ~30-32 chars before cutoff
  // Use fixed values based on label width for more predictable results
  let maxCharsPerLine
  if (widthMm <= 30) {
    maxCharsPerLine = 22 // Safe for 30mm labels
  } else if (widthMm <= 40) {
    maxCharsPerLine = 28 // Safe for 40mm labels (tested: 30-32 chars get cut, use 28)
  } else {
    maxCharsPerLine = Math.max(20, Math.floor(availableWidthDots / 9)) // Use 9 dots per char for larger labels
  }
  const lineHeight = 20 // 20 dots spacing between lines (~2.5mm for 203 DPI)

  // Y coordinates from top (in dots)
  // Start from top: Y=10 dots (~1.25mm for 203 DPI)
  let currentY = 10 // Start from top

  // Product name with sequence (FIRST - at the top, LEFT aligned, with wrap)
  const productLabel = `(${labelData.sequence}/${labelData.qty}) ${labelData.productName || '-'}`
  const productLines = wrapWords(productLabel, maxCharsPerLine)

  // Debug: log wrap result (always log to debug)
  console.log('ZPL Wrap Debug:', {
    productLabel,
    productLabelLength: productLabel.length,
    maxCharsPerLine,
    linesCount: productLines.length,
    lines: productLines,
    labelWidth: widthMm,
  })

  // Print each line of product name
  productLines.forEach((line) => {
    if (line && line.trim()) {
      const escapedProduct = escapeZPL(line.trim())
      // ^FOx,y^A0N,height,width^FDtext^FS
      commands.push(
        `^FO${leftX},${currentY}^A0N,${fontHeight},${fontWidth}^FD${escapedProduct}^FS\n`,
      )
      currentY += lineHeight // Move down for next line
    }
  })

  // Description if exists (SECOND - below product name, LEFT aligned)
  if (labelData.desc) {
    const descLine = `=> ${labelData.desc}`
    const descLines = wrapWords(descLine, maxCharsPerLine)

    // Print each line of description
    descLines.forEach((line) => {
      if (line && line.trim()) {
        const escapedDesc = escapeZPL(line.trim())
        commands.push(
          `^FO${leftX},${currentY}^A0N,${fontHeight},${fontWidth}^FD${escapedDesc}^FS\n`,
        )
        currentY += lineHeight // Move down for next line
      }
    })
  }

  // Contact name if exists (THIRD - below description, LEFT aligned)
  if (labelData.contactName) {
    const contactLine = `=> ${labelData.contactName}`
    const contactLines = wrapWords(contactLine, maxCharsPerLine)

    // Print each line of contact name
    contactLines.forEach((line) => {
      if (line && line.trim()) {
        const escapedContact = escapeZPL(line.trim())
        commands.push(
          `^FO${leftX},${currentY}^A0N,${fontHeight},${fontWidth}^FD${escapedContact}^FS\n`,
        )
        currentY += lineHeight // Move down for next line
      }
    })
  }

  // Detail meja (order Table) if exists — below contact, LEFT aligned
  if (labelData.tableLabel) {
    const tableLine = `=> ${labelData.tableLabel}`
    const tableLines = wrapWords(tableLine, maxCharsPerLine)

    tableLines.forEach((line) => {
      if (line && line.trim()) {
        const escapedTable = escapeZPL(line.trim())
        commands.push(
          `^FO${leftX},${currentY}^A0N,${fontHeight},${fontWidth}^FD${escapedTable}^FS\n`,
        )
        currentY += lineHeight // Move down for next line
      }
    })
  }

  // Border line (FOURTH - horizontal line below product/desc/contact)
  const borderY = currentY + 10 // Add 10 dots (~1.25mm) space before border
  const borderThickness = mmToDots(0.5, dpi)
  const borderWidth = widthDots - mmToDots(4, dpi) // Leave 2mm padding on each side
  // ^FOx,y^GBwidth,height,thickness^FS (GB = Graphic Box, but we use it as line)
  commands.push(
    `^FO${leftX},${borderY}^GB${borderWidth},${borderThickness},${borderThickness}^FS\n`,
  )

  // Timestamp (FIFTH - below border, LEFT aligned)
  const ts = labelData.timestampText || dayjs().format('DD MMM YYYY HH:mm')
  const tsText = `### ${ts} ###`
  const escapedTs = escapeZPL(tsText)
  const timestampY = borderY + 15 // 15 dots (~1.9mm) below border
  // Left align timestamp
  commands.push(`^FO${leftX},${timestampY}^A0N,${fontHeight},${fontWidth}^FD${escapedTs}^FS\n`)

  commands.push('^XZ\n') // End Label Format

  return commands
}

/**
 * Build labeling commands for ZPL format
 * This function handles multiple labels (one per item qty) and returns QZ Tray compatible format
 * @param {Object} receiptData - Receipt data with items
 * @param {Object} options - Print options { paperWidth, dpi, invert }
 * @returns {Array} Array of ZPL command strings for QZ Tray
 */
export const buildLabelingCommands = (receiptData, options = {}) => {
  const { paperWidth, dpi = 203, invert = false, outletOptions = {} } = options

  const items = receiptData?.items || []
  const zplCommands = []

  // Bawaan aktif: hanya '0' yang menyembunyikan deskripsi item
  const showItemDescription =
    outletOptions?.print_style_show_product_description?.toString() !== '0'

  // Process each item and create ZPL commands for each sequence
  items.forEach((item) => {
    const qty = Math.abs(item.qty || 1)
    const sequences = Array.from({ length: qty }, (_, idx) => idx + 1)

    sequences.forEach((sequence) => {
      const labelData = {
        qty,
        productName: item.custom_product_name || item.product?.name || '-',
        sequence,
        desc: showItemDescription ? (item.desc ?? item.description) : undefined,
        contactName: isOptionEnabled(outletOptions?.print_style_show_customer)
          ? receiptData.contact?.name
          : undefined,
        tableLabel:
          outletOptions?.print_style_show_order_type === '1'
            ? getTableReceiptLabel(receiptData)
            : undefined,
        timestampText: dayjs().format('DD MMM YYYY HH:mm'),
      }

      // buildZPLLabel now returns array directly (like buildReceiptCommands)
      const zplLabelCommands = buildZPLLabel(labelData, {
        paperWidth,
        dpi,
        invert,
      })

      // Push array of strings for this label
      zplCommands.push(zplLabelCommands)
    })
  })

  return zplCommands
}

export default {
  buildZPLLabel,
  buildLabelingCommands,
  getLabelDimensions,
  LABEL_PRESETS,
}
